// =============================================================================
// Reports Page - Report Avanzato Utilizzo Risorse
// =============================================================================

import { useState, useMemo } from 'react';
import {
  BarChart3,
  Loader2,
  AlertCircle,
  Filter,
  Download,
  Clock,
  CalendarCheck,
  Users,
  TrendingUp,
  Activity,
  CalendarClock,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  useResources,
  useResourceTypes,
  useReportsUtilization,
  useReportsPeakHours,
  type ReportsUtilizationParams,
} from '../api/hooks';
import { useTranslation } from 'react-i18next';
import { DatePickerInput } from '../components/ui/DatePickerInput';
import { PeakHoursHeatmap } from '../components/reports/PeakHoursHeatmap';

// ===========================================================================
// Constants
// ===========================================================================

const CHART_COLORS = [
  '#10b981', // emerald-500
  '#3b82f6', // blue-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
  '#ec4899', // pink-500
];

const GROUP_OPTIONS: { value: ReportsUtilizationParams['groupBy']; labelKey: string }[] = [
  { value: 'day', labelKey: 'groupBy.day' },
  { value: 'week', labelKey: 'groupBy.week' },
  { value: 'month', labelKey: 'groupBy.month' },
];

type ChartType = 'bar' | 'line';

// ===========================================================================
// Helpers
// ===========================================================================

function getDefaultDateRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

// ===========================================================================
// Chart Tooltip
// ===========================================================================

function ReportTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 text-sm">
      <p className="font-semibold text-gray-900 mb-1.5">{label}</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} className="flex items-center gap-2 text-gray-500 leading-relaxed">
          <span
            className="inline-block w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          {entry.name}:{' '}
          <span className="font-medium text-gray-900">{entry.value.toFixed(1)}h</span>
        </p>
      ))}
    </div>
  );
}

// ===========================================================================
// Main Component
// ===========================================================================

export function ReportsPage() {
  const { t, i18n: i18nInstance } = useTranslation('reports');
  // --- Filters state ---
  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<ReportsUtilizationParams['groupBy']>('week');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [reportType, setReportType] = useState<'utilization' | 'peak'>('utilization');

  // --- Data hooks ---
  const { data: resources = [], isLoading: loadingResources } = useResources();
  const { data: resourceTypes = [] } = useResourceTypes();

  const params: ReportsUtilizationParams = useMemo(
    () => ({
      startDate,
      endDate,
      resourceIds: selectedResourceIds.length > 0 ? selectedResourceIds : undefined,
      groupBy,
    }),
    [startDate, endDate, selectedResourceIds, groupBy],
  );

  const {
    data: report,
    isLoading: loadingReport,
    isError,
    error,
  } = useReportsUtilization(params);

  // --- Peak hours (heatmap) ---
  const {
    data: peak,
    isLoading: loadingPeak,
    isError: isPeakError,
  } = useReportsPeakHours({ startDate, endDate });

  // Nomi brevi giorni (lun→dom) per il callout "ora di punta"
  const peakDayLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18nInstance.language === 'it' ? 'it-IT' : 'en-US', {
      weekday: 'short',
    });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  }, [i18nInstance.language]);

  // Mappa colori locali dalle risorse caricate (useResources include resourceType)
  const localResourceColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of resources) {
      map[r.name] = r.color ?? r.resourceType?.color ?? '';
    }
    return map;
  }, [resources]);

  // Color map per risorsa — priorità: colore risorsa > colore tipo risorsa > colore tipo da report > fallback palette
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    (report?.resources ?? []).forEach((r, i) => {
      map[r.name] = localResourceColorMap[r.name]
        || r.color
        || r.resourceTypeColor
        || CHART_COLORS[i % CHART_COLORS.length];
    });
    return map;
  }, [report?.resources, localResourceColorMap]);

  const resourceNames = report?.resources.map((r) => r.name) ?? [];

  // --- Resource toggle ---
  const toggleResource = (id: string) => {
    setSelectedResourceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAll = () => setSelectedResourceIds([]);

  // --- Raggruppamento risorse per tipo ---
  const resourcesByType = useMemo(() => {
    const map = new Map<string, typeof resources>();
    for (const r of resources) {
      const typeId = r.resourceTypeId;
      if (!map.has(typeId)) map.set(typeId, []);
      map.get(typeId)!.push(r);
    }
    return map;
  }, [resources]);

  const toggleTypeResources = (typeId: string) => {
    const typeResources = resourcesByType.get(typeId) ?? [];
    const typeIds = typeResources.map((r) => r.id);

    if (selectedResourceIds.length === 0) {
      // Tutte selezionate → deseleziona questo tipo = seleziona tutti GLI ALTRI
      const otherIds = resources.filter((r) => r.resourceTypeId !== typeId).map((r) => r.id);
      setSelectedResourceIds(otherIds.length > 0 ? otherIds : []);
      return;
    }

    const allTypeSelected = typeIds.every((id) => selectedResourceIds.includes(id));
    if (allTypeSelected) {
      // Deseleziona tutte le risorse del tipo
      const remaining = selectedResourceIds.filter((id) => !typeIds.includes(id));
      setSelectedResourceIds(remaining);
    } else {
      // Seleziona tutte le risorse del tipo
      const merged = [...new Set([...selectedResourceIds, ...typeIds])];
      // Se ora tutte le risorse sono selezionate, resetta a []
      if (merged.length >= resources.length) {
        setSelectedResourceIds([]);
      } else {
        setSelectedResourceIds(merged);
      }
    }
  };

  const isTypeFullySelected = (typeId: string): boolean => {
    if (selectedResourceIds.length === 0) return true;
    const typeResources = resourcesByType.get(typeId) ?? [];
    return typeResources.every((r) => selectedResourceIds.includes(r.id));
  };

  // --- CSV Export ---
  const handleExport = () => {
    if (!report?.data.length) return;
    const headers = ['Periodo', ...resourceNames];
    const rows = report.data.map((row) =>
      [row.period, ...resourceNames.map((n) => String(row[n] ?? 0))].join(','),
    );
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `utilizzo-risorse_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-6 py-4 flex items-center gap-4">
          <div className="p-2 bg-emerald-50 rounded-lg">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{t('title')}</h1>
            <p className="text-sm text-gray-500">{t('subtitle')}</p>
          </div>

          {/* Toggle tipo report + Export */}
          <div className="ml-auto flex items-center gap-3">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setReportType('utilization')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  reportType === 'utilization'
                    ? 'bg-white text-emerald-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                {t('tabs.utilization')}
              </button>
              <button
                onClick={() => setReportType('peak')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  reportType === 'peak'
                    ? 'bg-white text-emerald-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <CalendarClock className="w-4 h-4" />
                {t('tabs.peak')}
              </button>
            </div>

            {reportType === 'utilization' && (
              <button
                onClick={handleExport}
                disabled={!report?.data.length}
                className="btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-40"
              >
                <Download className="w-4 h-4" />
                {t('exportCsv')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {reportType === 'utilization' && (<>
          {/* ============================================================= */}
          {/* Filter Bar                                                     */}
          {/* ============================================================= */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 w-full"
            >
              <Filter className="w-4 h-4 text-gray-400" />
              {t('filters.title')}
              <svg
                className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              {selectedResourceIds.length > 0 && (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                  {selectedResourceIds.length} {t('filters.selected')}
                </span>
              )}
            </button>

            {filtersOpen && (<>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Date range */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.startDate')}</label>
                <DatePickerInput
                  value={startDate}
                  onChange={setStartDate}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.endDate')}</label>
                <DatePickerInput
                  value={endDate}
                  onChange={setEndDate}
                />
              </div>

              {/* GroupBy */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('groupBy.label')}</label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as ReportsUtilizationParams['groupBy'])}
                  className="w-full px-3 py-2 text-sm input outline-none bg-white transition-colors"
                >
                  {GROUP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Chart type */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.chartType')}</label>
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setChartType('bar')}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                      chartType === 'bar'
                        ? 'bg-emerald-50 text-emerald-700 border-r border-gray-300'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border-r border-gray-300'
                    }`}
                  >
                    {t('filters.bars')}
                  </button>
                  <button
                    onClick={() => setChartType('line')}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                      chartType === 'line'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t('filters.lines')}
                  </button>
                </div>
              </div>
            </div>

            {/* Resource multi-select chips grouped by type */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-medium text-gray-500">{t('filters.resources')}</span>
                <button
                  onClick={selectAll}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
                >
                  {selectedResourceIds.length === 0 ? t('filters.allSelected') : t('filters.selectAll')}
                </button>
              </div>

              {loadingResources ? (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t('filters.loadingResources')}
                </div>
              ) : (
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  {[...resourcesByType.entries()].map(([typeId, typeResources]) => {
                    const type = resourceTypes.find((t) => t.id === typeId);
                    const typeName = type?.name ?? t('common:status.other', 'Other');
                    const typeColor = type?.color ?? '#6B7280';
                    const fullySelected = isTypeFullySelected(typeId);

                    return (
                      <div key={typeId} className="flex flex-wrap items-center gap-1.5">
                        <button
                          onClick={() => toggleTypeResources(typeId)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md border transition-colors ${
                            fullySelected
                              ? 'border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100'
                              : 'border-dashed border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <span
                            className="w-2 h-2 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: typeColor }}
                          />
                          {typeName}
                        </button>
                        {typeResources.map((r) => {
                          const isSelected =
                            selectedResourceIds.length === 0 || selectedResourceIds.includes(r.id);
                          return (
                            <button
                              key={r.id}
                              onClick={() => toggleResource(r.id)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                                isSelected
                                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                  : 'bg-gray-50 border-gray-200 text-gray-400'
                              }`}
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{
                                  backgroundColor: isSelected
                                    ? (r.color || type?.color || '#10b981')
                                    : '#d1d5db',
                                }}
                              />
                              {r.name}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </>)}
          </div>

          {/* ============================================================= */}
          {/* KPI Summary Cards                                                */}
          {/* ============================================================= */}
          {report?.summary && !loadingReport && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <Clock className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-medium">{t('kpi.totalHours')}</p>
                    <p className="text-lg font-bold text-gray-900 tabular-nums">
                      {report.summary.totalHours.toFixed(1)}h
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <CalendarCheck className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-medium">{t('kpi.appointments')}</p>
                    <p className="text-lg font-bold text-gray-900 tabular-nums">
                      {report.summary.totalAppointments}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-50 rounded-lg">
                    <Users className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-medium">{t('kpi.avgPerResource')}</p>
                    <p className="text-lg font-bold text-gray-900 tabular-nums">
                      {report.summary.averageHoursPerResource.toFixed(1)}h
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-medium">{t('kpi.mostActive')}</p>
                    {report.summary.busiestResource ? (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: report.summary.busiestResource.color ?? '#10b981' }}
                        />
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {report.summary.busiestResource.name}
                        </p>
                        <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                          {report.summary.busiestResource.hours}h
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">—</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-50 rounded-lg">
                    <Activity className="w-4 h-4 text-cyan-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-medium">{t('kpi.dailyAvg')}</p>
                    <p className="text-lg font-bold text-gray-900 tabular-nums">
                      {report.summary.averageHoursPerDay.toFixed(1)}h
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============================================================= */}
          {/* Chart                                                          */}
          {/* ============================================================= */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  {t('chart.title')}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {report?.params
                    ? `${new Date(report.params.startDate).toLocaleDateString(i18nInstance.language === 'it' ? 'it-IT' : 'en-US')} \u2013 ${new Date(report.params.endDate).toLocaleDateString(i18nInstance.language === 'it' ? 'it-IT' : 'en-US')}`
                    : t('chart.dateRangeHint')}
                </p>
              </div>
              {loadingReport && (
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              )}
            </div>

            {isError ? (
              <div className="flex items-center justify-center gap-2 py-20 text-red-500">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">
                  {(error as any)?.message || t('chart.loadError')}
                </span>
              </div>
            ) : !report?.data.length && !loadingReport ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <BarChart3 className="w-10 h-10 mb-2" />
                <p className="text-sm font-medium">{t('chart.noData')}</p>
                <p className="text-xs mt-1">{t('chart.noDataHint')}</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={420}>
                {chartType === 'bar' ? (
                  <BarChart data={report?.data ?? []} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis
                      dataKey="period"
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      label={{
                        value: t('chart.yAxisLabel'),
                        angle: -90,
                        position: 'insideLeft',
                        style: { fill: '#94a3b8', fontSize: 12 },
                      }}
                    />
                    <Tooltip content={<ReportTooltip />} />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ paddingTop: 16, fontSize: 13 }}
                    />
                    {resourceNames.map((name) => (
                      <Bar
                        key={name}
                        dataKey={name}
                        fill={colorMap[name]}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                      />
                    ))}
                  </BarChart>
                ) : (
                  <LineChart data={report?.data ?? []} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis
                      dataKey="period"
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      label={{
                        value: t('chart.yAxisLabel'),
                        angle: -90,
                        position: 'insideLeft',
                        style: { fill: '#94a3b8', fontSize: 12 },
                      }}
                    />
                    <Tooltip content={<ReportTooltip />} />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ paddingTop: 16, fontSize: 13 }}
                    />
                    {resourceNames.map((name) => (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={colorMap[name]}
                        strokeWidth={2}
                        dot={{ r: 4, fill: colorMap[name] }}
                        activeDot={{ r: 6 }}
                      />
                    ))}
                  </LineChart>
                )}
              </ResponsiveContainer>
            )}
          </div>

          {/* ============================================================= */}
          {/* Summary Table                                                  */}
          {/* ============================================================= */}
          {report?.data && report.data.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">{t('detail.title')}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-6 py-3 font-medium text-gray-500 bg-gray-50/50">
                        {t('detail.period')}
                      </th>
                      {resourceNames.map((name) => (
                        <th
                          key={name}
                          className="text-right px-6 py-3 font-medium text-gray-500 bg-gray-50/50"
                        >
                          <span className="flex items-center justify-end gap-2">
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{ backgroundColor: colorMap[name] }}
                            />
                            {name}
                          </span>
                        </th>
                      ))}
                      <th className="text-right px-6 py-3 font-medium text-gray-900 bg-gray-50/50">
                        {t('detail.total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.data.map((row, idx) => {
                      const total = resourceNames.reduce(
                        (sum, n) => sum + (Number(row[n]) || 0),
                        0,
                      );
                      return (
                        <tr key={idx} className="border-b border-gray-100 last:border-0">
                          <td className="px-6 py-3 font-medium text-gray-900">{row.period}</td>
                          {resourceNames.map((name) => (
                            <td key={name} className="px-6 py-3 text-right text-gray-600 tabular-nums">
                              {Number(row[name] || 0).toFixed(1)}h
                            </td>
                          ))}
                          <td className="px-6 py-3 text-right font-semibold text-gray-900 tabular-nums">
                            {total.toFixed(1)}h
                          </td>
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    <tr className="bg-gray-50/50 border-t border-gray-200">
                      <td className="px-6 py-3 font-semibold text-gray-900">{t('detail.total')}</td>
                      {resourceNames.map((name) => {
                        const colTotal = report.data.reduce(
                          (sum, row) => sum + (Number(row[name]) || 0),
                          0,
                        );
                        return (
                          <td key={name} className="px-6 py-3 text-right font-semibold text-gray-900 tabular-nums">
                            {colTotal.toFixed(1)}h
                          </td>
                        );
                      })}
                      <td className="px-6 py-3 text-right font-bold text-emerald-600 tabular-nums">
                        {report.data
                          .reduce(
                            (grand, row) =>
                              grand + resourceNames.reduce((s, n) => s + (Number(row[n]) || 0), 0),
                            0,
                          )
                          .toFixed(1)}
                        h
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </>)}

          {/* ============================================================= */}
          {/* Peak Hours (heatmap ore di punta)                              */}
          {/* ============================================================= */}
          {reportType === 'peak' && (<>
            {/* Filtro date compatto + callout ora di punta */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.startDate')}</label>
                  <DatePickerInput value={startDate} onChange={setStartDate} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.endDate')}</label>
                  <DatePickerInput value={endDate} onChange={setEndDate} />
                </div>
                {peak?.busiestSlot && (
                  <div className="ml-auto flex items-center gap-2 text-sm">
                    <span className="text-gray-500">{t('peak.busiest')}</span>
                    <span className="font-semibold text-gray-900 capitalize">
                      {peakDayLabels[peak.busiestSlot.day]} {String(peak.busiestSlot.hour).padStart(2, '0')}:00
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold tabular-nums">
                      {peak.busiestSlot.count}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Heatmap */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <div className="mb-6">
                <h2 className="text-base font-semibold text-gray-900">{t('peak.title')}</h2>
                <p className="text-sm text-gray-500 mt-0.5">{t('peak.subtitle')}</p>
              </div>
              {isPeakError ? (
                <div className="flex items-center justify-center gap-2 py-20 text-red-500">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">{t('chart.loadError')}</span>
                </div>
              ) : (
                <PeakHoursHeatmap data={peak} loading={loadingPeak} />
              )}
            </div>
          </>)}
        </div>
      </main>
    </div>
  );
}

export default ReportsPage;
